'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  queryInventoryMovements,
  type InventoryMovementRow,
} from '@/hooks/useTomVendingInventory';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingMachinesList, type TomVendingMachine } from '@/hooks/useTomVendingMachine';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { useAuthData } from '@/hooks/getAuthData';

const PAGE_SIZE = 50;

const REASON_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All reasons' },
  { value: 'reject', label: 'reject' },
  { value: 'fill', label: 'fill' },
  { value: 'recycle', label: 'recycle' },
  { value: 'par', label: 'par' },
  { value: 'adjustment', label: 'adjustment' },
];

const filterMachineOptions = createFilterOptions<TomVendingMachine>({
  stringify: (m) =>
    `${m.assetId} ${m.title ?? ''} ${m.location?.name ?? ''} ${m.id}`.trim(),
});

type TomLocationRow = { id: number; name?: string; isWarehouse?: boolean };

const filterLocationOptions = createFilterOptions<TomLocationRow>({
  stringify: (l) => `${l.name ?? ''} ${l.id}`.trim(),
});

function formatEndpoint(
  kind: string | null,
  label: string | null,
  vmId: number | null,
): string {
  if (kind === 'warehouse') {
    return label?.trim() || 'Warehouse';
  }
  if (kind === 'vending_machine') {
    const t = label?.trim() || '';
    const idPart = vmId != null ? `#${vmId}` : '';
    if (t && idPart) return `${t} ${idPart}`;
    if (t) return t;
    if (idPart) return `Machine ${idPart}`;
    return 'Vending machine';
  }
  return '—';
}

function endpointsLabel(row: InventoryMovementRow): { from: string; to: string } {
  return {
    from:
      row.fromSiteId != null
        ? formatEndpoint(row.fromKind, row.fromLabel, row.fromVendingMachineId)
        : '—',
    to:
      row.toSiteId != null
        ? formatEndpoint(row.toKind, row.toLabel, row.toVendingMachineId)
        : '—',
  };
}

const TomsVendingInventoryMovementsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialVm = parseInt(String(searchParams.get('vendingMachineId') || ''), 10);
  const initialLoc = parseInt(String(searchParams.get('locationId') || ''), 10);
  const initialReason = String(searchParams.get('reason') ?? '').trim();

  const [data, setData] = useState<InventoryMovementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const [filterVm, setFilterVm] = useState<number | ''>(Number.isNaN(initialVm) ? '' : initialVm);
  const [filterLoc, setFilterLoc] = useState<number | ''>(Number.isNaN(initialLoc) ? '' : initialLoc);
  const [filterReason, setFilterReason] = useState<string>(initialReason);

  const { data: machines = [], isLoading: machinesLoading } = useTomVendingMachinesList();
  const { data: locations = [], isLoading: locationsLoading } = useTomVendingLocationsList();

  const { showError } = useNotification();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');

  const selectedMachine = useMemo(
    () =>
      filterVm === '' || filterVm === null || Number.isNaN(Number(filterVm))
        ? null
        : machines.find((m) => m.id === filterVm) ?? null,
    [machines, filterVm],
  );

  const selectedLocation = useMemo(
    () =>
      filterLoc === '' || filterLoc === null || Number.isNaN(Number(filterLoc))
        ? null
        : (locations as TomLocationRow[]).find((l) => l.id === filterLoc) ?? null,
    [locations, filterLoc],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const vm =
        filterVm === '' || filterVm === null || Number.isNaN(Number(filterVm))
          ? undefined
          : Number(filterVm);
      const loc =
        filterLoc === '' || filterLoc === null || Number.isNaN(Number(filterLoc))
          ? undefined
          : Number(filterLoc);
      const r = await queryInventoryMovements({
        vendingMachineId: vm,
        locationId: loc,
        reason: filterReason.trim() || undefined,
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setData(r.data);
      setTotal(r.total);
    } catch (e: any) {
      showError(e?.message || 'Failed to load movements');
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [filterVm, filterLoc, filterReason, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<InventoryMovementRow>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'When',
        size: 160,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string;
          return v ? new Date(v).toLocaleString() : '—';
        },
      },
      {
        id: 'user',
        header: 'User',
        size: 160,
        Cell: ({ row }) => {
          const r = row.original;
          if (r.userDisplayName?.trim()) return r.userDisplayName;
          if (r.userEmail?.trim()) return r.userEmail;
          if (r.userId != null) return `User #${r.userId}`;
          return '—';
        },
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 120,
      },
      {
        accessorKey: 'itemName',
        header: 'Product',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'quantity',
        header: 'Qty',
        size: 72,
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        size: 100,
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        id: 'from',
        header: 'From',
        size: 200,
        Cell: ({ row }) => endpointsLabel(row.original).from,
      },
      {
        id: 'to',
        header: 'To',
        size: 200,
        Cell: ({ row }) => endpointsLabel(row.original).to,
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Inventory', href: '/app/toms-vending/inventory' },
    { label: 'Stock movements' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Stock movements" />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Every change to stocked quantity is logged with time, user, and from / to storage location
          (warehouse, a vending machine, or blank when the other side is not recorded).
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-end' }}>
          <Autocomplete<TomVendingMachine>
            size="small"
            sx={{ minWidth: 280, maxWidth: 360 }}
            options={machines}
            loading={machinesLoading}
            value={selectedMachine}
            onChange={(_, v) => {
              setFilterVm(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(m) => m.assetId}
            filterOptions={filterMachineOptions}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, m) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{m.assetId}</Typography>
                  {(m.title?.trim() || m.location?.name) && (
                    <Typography variant="caption" color="text.secondary">
                      {[m.title?.trim(), m.location?.name].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Vending machine" placeholder="Search asset ID…" />
            )}
          />
          <Autocomplete<TomLocationRow>
            size="small"
            sx={{ minWidth: 280, maxWidth: 360 }}
            options={locations as TomLocationRow[]}
            loading={locationsLoading}
            value={selectedLocation}
            onChange={(_, v) => {
              setFilterLoc(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            filterOptions={filterLocationOptions}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, l) => (
              <li {...props} key={l.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{l.name?.trim() || `Location ${l.id}`}</Typography>
                  {tomVendingLocationIsWarehouse(l) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="Search location name…" />
            )}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="movement-reason-filter">Reason</InputLabel>
            <Select
              labelId="movement-reason-filter"
              label="Reason"
              value={filterReason}
              onChange={(e) => {
                setFilterReason(String(e.target.value));
                setPageIndex(0);
              }}
            >
              {REASON_FILTER_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() => {
              setFilterVm('');
              setFilterLoc('');
              setFilterReason('');
              setPageIndex(0);
            }}
          >
            Clear filters
          </Button>
          {hasAdminRole && (
            <Button component={RouterLink} to="/app/toms-vending/inventory/reject-summary" variant="text" size="small" sx={{ textTransform: 'none' }}>
              Reject totals
            </Button>
          )}
          <Button component={RouterLink} to="/app/toms-vending/inventory" variant="text" size="small" sx={{ textTransform: 'none' }}>
            Catalog
          </Button>
        </Box>

        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          rowCount={total}
          enableGlobalFilter={false}
          onPaginationChange={(updater) => {
            setPageIndex(
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize: PAGE_SIZE }).pageIndex
                : updater.pageIndex,
            );
          }}
          state={{
            isLoading,
            pagination: { pageIndex, pageSize: PAGE_SIZE },
          }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingInventoryMovementsPage;
